import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { getProviderCategories, adminGetProviders, adminCreateProvider, adminUpdateProvider, adminDeleteProvider } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ShieldCheck, Power, PowerOff } from 'lucide-react';

const EMPTY = { category: '', name: '', city: '', service_area: '', phone: '', whatsapp: '', website: '', description: '', source_note: '', verified: false, status: 'active' };

const ProvidersManager = () => {
  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCat, setFilterCat] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => {
    setLoading(true);
    adminGetProviders(filterCat === 'all' ? {} : { category: filterCat }).then(setRows).catch(() => toast.error('Failed to load providers')).finally(() => setLoading(false));
  }, [filterCat]);

  useEffect(() => { getProviderCategories().then(setCategories).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); };

  const save = async () => {
    if (!form.category || !form.name) return toast.error('Category and name are required');
    try {
      if (editing) { await adminUpdateProvider(editing.provider_id, form); toast.success('Provider updated'); }
      else { await adminCreateProvider(form); toast.success('Provider added'); }
      setOpen(false); load();
    } catch (e) { toast.error(e.message); }
  };

  const toggleStatus = async (r) => {
    const next = r.status === 'active' ? 'disabled' : 'active';
    try { await adminUpdateProvider(r.provider_id, { status: next }); load(); } catch (e) { toast.error(e.message); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try { await adminDeleteProvider(r.provider_id); toast.success('Deleted'); load(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="admin-providers-manager">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-60" data-testid="providers-filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-500">{rows.length} providers</span>
        <Button onClick={openNew} className="ml-auto rounded-full bg-[#0F172A] text-white" data-testid="providers-add-btn"><Plus className="h-4 w-4 mr-1.5" /> Add Provider</Button>
      </div>

      {loading ? <p className="text-slate-500 py-8">Loading…</p> : rows.length === 0 ? <p className="text-slate-500 py-8">No providers yet. Click "Add Provider".</p> : (
        <div className="overflow-x-auto border border-slate-200 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-left"><tr>
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Category</th><th className="px-4 py-3">Area</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.provider_id} className="border-t border-slate-100" data-testid={`provider-row-${r.provider_id}`}>
                  <td className="px-4 py-3 font-medium text-[#0F172A]">{r.name}{r.verified && <ShieldCheck className="inline h-3.5 w-3.5 ml-1 text-[#1D4ED8]" />}</td>
                  <td className="px-4 py-3 text-slate-600">{r.category_label}</td>
                  <td className="px-4 py-3 text-slate-600">{r.service_area || r.city || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{r.phone || '—'}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.status === 'active' ? 'bg-emerald-50 text-emerald-700' : r.status === 'pending' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{r.status}</span></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => toggleStatus(r)} title={r.status === 'active' ? 'Disable' : 'Enable'} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid={`provider-toggle-${r.provider_id}`}>{r.status === 'active' ? <PowerOff className="h-4 w-4 text-slate-500" /> : <Power className="h-4 w-4 text-emerald-600" />}</button>
                    <button onClick={() => openEdit(r)} className="p-1.5 hover:bg-slate-100 rounded-md" data-testid={`provider-edit-${r.provider_id}`}><Pencil className="h-4 w-4 text-slate-500" /></button>
                    <button onClick={() => remove(r)} className="p-1.5 hover:bg-red-50 rounded-md" data-testid={`provider-delete-${r.provider_id}`}><Trash2 className="h-4 w-4 text-red-500" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit provider' : 'Add provider'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="provider-form-category"><SelectValue placeholder="Category *" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Business / professional name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="provider-form-name" />
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              <Input placeholder="Service area (e.g. Pan India)" value={form.service_area} onChange={(e) => setForm({ ...form, service_area: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="provider-form-phone" />
              <Input placeholder="WhatsApp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} />
            </div>
            <Input placeholder="Website (https://...)" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            <Textarea placeholder="Short description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <Input placeholder="Source / reference (where contact was published)" value={form.source_note} onChange={(e) => setForm({ ...form, source_note: e.target.value })} />
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.verified} onChange={(e) => setForm({ ...form, verified: e.target.checked })} /> Verified</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="disabled">Disabled</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-[#0F172A] text-white" data-testid="provider-form-save">{editing ? 'Update' : 'Add'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProvidersManager;
